import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSidebar } from '../../contexts/SidebarContext';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useNotification } from '../../contexts/NotificationContext';
import Header from '../../components/ui/Header';
import EnhancedSidebar from '../../components/ui/EnhancedSidebar';
import EnhancedBreadcrumb from '../../components/ui/EnhancedBreadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import CustomerTable from './components/CustomerTable';
import CustomerFilters from './components/CustomerFilters';
import AddCustomerModal from '../crm/components/AddCustomerModal';
import EditCustomerModal from '../crm/components/EditCustomerModal';
import DeleteContactModal from './components/DeleteContactModal';
import CustomerDetailModal from './components/CustomerDetailModal';
import customerService from '../../utils/customerService';

const ClientsPage = () => {
  const navigate = useNavigate();
  const { sidebarExpanded, handleSidebarToggle } = useSidebar();
  const { currentOrganization } = useOrganization();
  const { addNotification } = useNotification();
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [customerToEdit, setCustomerToEdit] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [customerDetailModalOpen, setCustomerDetailModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    customerType: '',
    pipelineStage: '',
    priority: '',
    status: 'active',
    serviceType: '',
    location: '',
    dateRange: '',
    customerTier: '',
    leadSource: ''
  });
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    archived: 0,
    individuals: 0,
    organizations: 0,
    totalEstimatedValue: 0,
    byStage: {}
  });

  // Load customers from database (Clients optimized)
  const loadCustomers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await customerService.getClientsTableData(filters);
      setCustomers(data);
    } catch (err) {
      console.error('Error loading customers:', err);
      setError('Failed to load customers. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Load customer statistics (Clients optimized)
  const loadStats = async () => {
    try {
      const statsData = await customerService.getClientsStats(currentOrganization?.id);
      setStats(statsData);
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  // Initial data load
  useEffect(() => {
    if (currentOrganization?.id) {
      loadCustomers();
      loadStats();
    }
  }, [currentOrganization?.id]);

  // Reload customers when filters change
  useEffect(() => {
    if (currentOrganization?.id) {
      loadCustomers();
    }
  }, [filters, currentOrganization?.id]);

  // Auto-archive customers periodically
  useEffect(() => {
    if (!currentOrganization?.id) return;

    // Run auto-archive on mount
    customerService.autoArchiveCustomers().catch(console.error);

    // Set up interval to run auto-archive every hour
    const interval = setInterval(() => {
      customerService.autoArchiveCustomers().catch(console.error);
    }, 60 * 60 * 1000); // 1 hour

    return () => clearInterval(interval);
  }, [currentOrganization?.id]);

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
  };

  const handleAddCustomer = async (newCustomerData) => {
    try {
      await loadCustomers();
      await loadStats();
      setShowAddModal(false);
    } catch (err) {
      console.error('Error refreshing customer data:', err);
      setError('Failed to refresh customer data. Please try again.');
    }
  };

  const handleEditCustomer = (customer) => {
    setCustomerToEdit(customer);
    setEditModalOpen(true);
  };

  const handleUpdateCustomer = async (updatedCustomerData) => {
    try {
      await customerService.updateContact(customerToEdit.id, updatedCustomerData);
      await loadCustomers();
      await loadStats();
      setEditModalOpen(false);
      setCustomerToEdit(null);
    } catch (err) {
      console.error('Error updating customer:', err);
      setError('Failed to update customer. Please try again.');
    }
  };

  const handleSendReviewRequest = (customer) => {
    console.log('Send review request for:', customer);
    addNotification('Review request functionality coming soon', 'info');
  };

  const handleViewReviews = (customer) => {
    console.log('View reviews for:', customer);
    navigate('/reviews', { state: { customerId: customer.id } });
  };

  const handleDeleteCustomer = (customer) => {
    setCustomerToDelete(customer);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async (customerId) => {
    try {
      await customerService.deleteContactWithValidation(customerId);
      await loadCustomers();
      await loadStats();
      setDeleteModalOpen(false);
      setCustomerToDelete(null);
    } catch (err) {
      console.error('Error deleting customer:', err);
      setError('Failed to delete customer. Please try again.');
    }
  };

  const handleArchiveCustomer = async (customerId, reason = 'Manual archive') => {
    try {
      await customerService.archiveContact(customerId, reason);
      await loadCustomers();
      await loadStats();
    } catch (err) {
      console.error('Error archiving customer:', err);
      setError('Failed to archive customer. Please try again.');
    }
  };

  const handleBulkArchive = async (customerIds, reason = 'Bulk archive') => {
    try {
      await customerService.bulkArchiveContacts(customerIds, reason);
      await loadCustomers();
      await loadStats();
    } catch (err) {
      console.error('Error bulk archiving customers:', err);
      setError('Failed to archive customers. Please try again.');
    }
  };

  const handleRestoreCustomer = async (customerId) => {
    try {
      await customerService.restoreContact(customerId);
      await loadCustomers();
      await loadStats();
    } catch (err) {
      console.error('Error restoring customer:', err);
      setError('Failed to restore customer. Please try again.');
    }
  };

  const handleBulkRestore = async (customerIds) => {
    try {
      await customerService.bulkRestoreContacts(customerIds);
      await loadCustomers();
      await loadStats();
    } catch (err) {
      console.error('Error bulk restoring customers:', err);
      setError('Failed to restore customers. Please try again.');
    }
  };

  const handleOpenCustomerView = (customer) => {
    setSelectedCustomer(customer);
    setCustomerDetailModalOpen(true);
  };

  // Show loading state
  if (loading && customers.length === 0) {
    return (
      <div className="min-h-screen bg-background">
        <Header 
          onSidebarToggle={handleSidebarToggle}
          sidebarExpanded={sidebarExpanded}
        />
        <EnhancedSidebar 
          expanded={sidebarExpanded}
          onToggle={handleSidebarToggle}
        />
        
        <main className={`pt-16 transition-all duration-300 ease-in-out ${
          sidebarExpanded ? 'lg:pl-64' : 'lg:pl-16'
        }`}>
          <EnhancedBreadcrumb />
          <div className="p-6">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading clients...</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header 
        onSidebarToggle={handleSidebarToggle}
        sidebarExpanded={sidebarExpanded}
      />
      <EnhancedSidebar 
        expanded={sidebarExpanded}
        onToggle={handleSidebarToggle}
      />
      
      <main className={`pt-16 transition-all duration-300 ease-in-out ${
        sidebarExpanded ? 'lg:pl-64' : 'lg:pl-16'
      }`}>
        <EnhancedBreadcrumb />
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Client Management</h1>
              <p className="text-muted-foreground">Manage your client database and track customer information</p>
            </div>
            <div className="flex items-center space-x-3">
              <Button 
                variant="outline" 
                onClick={() => navigate('/crm')}
                className="flex items-center space-x-2"
              >
                <Icon name="GitBranch" size={16} />
                <span>View Pipeline</span>
              </Button>
              <Button 
                onClick={() => setShowAddModal(true)}
                className="flex items-center space-x-2"
              >
                <Icon name="Plus" size={16} />
                <span>Add Client</span>
              </Button>
            </div>
          </div>

          {/* Error State */}
          {error && (
            <div className="mb-6 p-4 bg-error-50 border border-error-200 rounded-lg">
              <div className="flex items-center space-x-2">
                <Icon name="AlertCircle" size={16} className="text-error" />
                <span className="text-error font-medium">{error}</span>
              </div>
            </div>
          )}

          {/* Client Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Clients</p>
                  <p className="text-2xl font-bold text-foreground">{stats.totalClients || 0}</p>
                </div>
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                  <Icon name="Users" size={24} className="text-primary" />
                </div>
              </div>
            </div>

            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Clients</p>
                  <p className="text-2xl font-bold text-foreground">{stats.activeClients || 0}</p>
                </div>
                <div className="w-12 h-12 bg-success-100 rounded-lg flex items-center justify-center">
                  <Icon name="CheckCircle" size={24} className="text-success" />
                </div>
              </div>
            </div>

            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Archived Clients</p>
                  <p className="text-2xl font-bold text-foreground">{stats.archivedClients || 0}</p>
                </div>
                <div className="w-12 h-12 bg-info-100 rounded-lg flex items-center justify-center">
                  <Icon name="Archive" size={24} className="text-info" />
                </div>
              </div>
            </div>

            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">New This Month</p>
                  <p className="text-2xl font-bold text-foreground">{stats.newThisMonth || 0}</p>
                </div>
                <div className="w-12 h-12 bg-warning-100 rounded-lg flex items-center justify-center">
                  <Icon name="Plus" size={24} className="text-warning" />
                </div>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="mb-6">
            <CustomerFilters 
              onFiltersChange={setFilters}
              activeFilters={filters}
            />
          </div>

          {/* Client Table */}
          <div className="w-full">
            <CustomerTable 
              customers={customers}
              onCustomerSelect={handleCustomerSelect}
              selectedCustomer={selectedCustomer}
              onEditCustomer={handleEditCustomer}
              onSendReviewRequest={handleSendReviewRequest}
              onViewReviews={handleViewReviews}
              onArchiveCustomer={handleArchiveCustomer}
              onBulkArchive={handleBulkArchive}
              onDeleteCustomer={handleDeleteCustomer}
              onRestoreCustomer={handleRestoreCustomer}
              onBulkRestore={handleBulkRestore}
              onOpenCustomerView={handleOpenCustomerView}
              loading={loading}
            />
          </div>
        </div>
      </main>

      {/* Modals */}
      {showAddModal && (
        <AddCustomerModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSave={handleAddCustomer}
        />
      )}

      {editModalOpen && customerToEdit && (
        <EditCustomerModal
          isOpen={editModalOpen}
          customer={customerToEdit}
          onClose={() => {
            setEditModalOpen(false);
            setCustomerToEdit(null);
          }}
          onSave={handleUpdateCustomer}
        />
      )}

      {deleteModalOpen && (
        <DeleteContactModal
          isOpen={deleteModalOpen}
          onClose={() => {
            setDeleteModalOpen(false);
            setCustomerToDelete(null);
          }}
          contact={customerToDelete}
          onConfirm={handleConfirmDelete}
        />
      )}

      {customerDetailModalOpen && (
        <CustomerDetailModal
          isOpen={customerDetailModalOpen}
          onClose={() => setCustomerDetailModalOpen(false)}
          customer={selectedCustomer}
          onEdit={handleEditCustomer}
          onSendReviewRequest={handleSendReviewRequest}
          onViewReviews={handleViewReviews}
        />
      )}
    </div>
  );
};

export default ClientsPage; 